import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import LandingHeader from '../components/LandingHeader'
import WhitepaperCTA from '../components/WhitepaperCTA'

// Feature Card Component
function FeatureCard({ icon, title, description, items }) {
  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100 hover:shadow-xl hover:border-purple-200 transition-all">
      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4 text-white">
        {icon}
      </div>
      <h3 className="text-xl font-bold text-slate-900 mb-2">{title}</h3>
      <p className="text-slate-600 mb-4">{description}</p>
      {items && (
        <ul className="space-y-2">
          {items.map((item, index) => (
            <li key={index} className="flex items-start gap-2 text-sm text-slate-600">
              <svg className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// Metric Card Component
function MetricCard({ value, label }) {
  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center border border-white/20">
      <div className="text-4xl font-bold text-white mb-2">{value}</div>
      <div className="text-purple-200 text-sm">{label}</div>
    </div>
  )
}

// Use Case Card Component
function UseCaseCard({ icon, title, description, examples }) {
  return (
    <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 border border-slate-700">
      <div className="text-4xl mb-4">{icon}</div>
      <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
      <p className="text-slate-400 mb-4">{description}</p>
      <div className="flex flex-wrap gap-2">
        {examples.map((example, index) => (
          <span key={index} className="text-xs bg-purple-500/20 text-purple-300 px-3 py-1 rounded-full">
            {example}
          </span>
        ))}
      </div>
    </div>
  )
}

export default function Features() {
  const { i18n } = useTranslation()
  const lang = i18n.language?.startsWith('es') ? 'es' : 'en'

  const content = {
    es: {
      hero: {
        badge: 'Plataforma v0.7',
        title: 'Machine Learning sobre Datos Encriptados',
        subtitle: 'La plataforma más completa para entrenar y ejecutar modelos de IA sobre información sensible utilizando Encriptación Homomórfica Completa (FHE).',
        cta: 'Solicitar Demo',
        docs: 'Ver Documentación'
      },
      metrics: {
        title: 'Métricas de la Plataforma',
        items: [
          { value: '559+', label: 'Tests Automatizados' },
          { value: '15+', label: 'Algoritmos ML' },
          { value: '42', label: 'Páginas Dashboard' },
          { value: '14', label: 'Apps Backend' },
          { value: '256-bit', label: 'Seguridad FHE' },
          { value: '69%', label: 'Cobertura de Código' }
        ]
      },
      mlCapabilities: {
        title: 'Capacidades de Machine Learning',
        subtitle: 'Más de 15 algoritmos optimizados para encriptación homomórfica',
        features: [
          {
            title: 'Deep Learning Encriptado',
            description: 'Redes neuronales con activaciones polinómicas diseñadas para operar sobre datos FHE.',
            items: ['Arquitecturas feedforward', 'Activaciones polinómicas', 'Backpropagation seguro']
          },
          {
            title: 'Métodos de Ensamble',
            description: 'Combine múltiples modelos para predicciones más robustas sin descifrar datos.',
            items: ['Random Forest', 'Gradient Boosting', 'Voting Classifier', 'Stacking']
          },
          {
            title: 'Detección de Anomalías',
            description: 'Identifique valores atípicos operando completamente sobre datos encriptados.',
            items: ['Isolation Forest', 'Local Outlier Factor', 'Detección estadística']
          },
          {
            title: 'Ingeniería de Features',
            description: 'Transformadores avanzados compatibles con FHE para preparación de datos.',
            items: ['Features polinómicas', 'Encoding categórico', 'Normalización cuantílica']
          }
        ]
      },
      dashboard: {
        title: 'Dashboard Empresarial',
        subtitle: '42 páginas diseñadas para equipos de datos y compliance',
        features: [
          {
            title: 'Constructor de Reportes',
            description: 'Genere informes de rendimiento, cumplimiento y auditoría.',
            items: ['Exportación PDF/CSV/Excel/JSON', 'Reportes programados', 'Templates personalizables']
          },
          {
            title: 'Automatización de Workflows',
            description: 'Configure pipelines de procesamiento con triggers automáticos.',
            items: ['Triggers programados', 'Eventos basados en datos', 'Notificaciones automáticas']
          },
          {
            title: 'Explorador de Datos',
            description: 'Visualice y analice datasets encriptados sin exponerlos.',
            items: ['Visualización segura', 'Estadísticas agregadas', 'Preview de datos']
          },
          {
            title: 'Monitoreo en Tiempo Real',
            description: 'Dashboards de salud del sistema y métricas de modelos.',
            items: ['Alertas configurables', 'Métricas de performance', 'Logs en tiempo real']
          }
        ]
      },
      apis: {
        title: 'APIs Empresariales',
        subtitle: 'Integración completa para sus sistemas',
        features: [
          {
            title: 'Predicción por Lotes',
            description: 'Procese miles de registros encriptados en una sola llamada API.',
            items: ['Scoring masivo', 'Procesamiento paralelo', 'Resultados asincrónicos']
          },
          {
            title: 'Versionado de Modelos',
            description: 'Gestione el ciclo de vida completo de sus modelos.',
            items: ['Training → Deploy → Monitor', 'Rollback automático', 'Trazabilidad completa']
          },
          {
            title: 'Webhooks',
            description: 'Integre con sistemas externos mediante notificaciones en tiempo real.',
            items: ['Eventos de modelos', 'Alertas de compliance', 'Integraciones custom']
          },
          {
            title: 'Compartición de Modelos',
            description: 'Colabore entre organizaciones sin exponer datos de entrenamiento.',
            items: ['Modelos pre-entrenados', 'Federación de consorcios', 'Transferencia segura']
          }
        ]
      },
      compliance: {
        title: 'Cumplimiento y Seguridad',
        items: [
          'Compatible con HIPAA, GDPR, PCI-DSS',
          'Niveles de seguridad: 128, 192 y 256 bits',
          'Logs de auditoría inmutables',
          'Integración blockchain opcional',
          'Arquitectura multi-tenant',
          'Aislamiento completo de datos'
        ]
      },
      useCases: {
        title: 'Casos de Uso por Industria',
        cases: [
          {
            icon: '🏥',
            title: 'Salud',
            description: 'Análisis predictivo sobre historiales clínicos encriptados.',
            examples: ['Riesgo cardiovascular', 'Detección temprana', 'Optimización tratamientos']
          },
          {
            icon: '🏦',
            title: 'Finanzas',
            description: 'Procesamiento de datos financieros sensibles sin exposición.',
            examples: ['Scoring crediticio', 'Detección de fraude', 'Análisis de riesgo']
          },
          {
            icon: '🏛️',
            title: 'Gobierno',
            description: 'Cumpliendo con las más estrictas regulaciones de privacidad.',
            examples: ['Datos tributarios', 'Detección anomalías', 'Predicción demanda']
          }
        ]
      },
      cta: {
        title: '¿Listo para Transformar sus Datos?',
        subtitle: 'Solicite una demo personalizada para su industria.',
        button: 'Solicitar Demo',
        contact: 'Contactar Ventas'
      }
    },
    en: {
      hero: {
        badge: 'Platform v0.7',
        title: 'Machine Learning on Encrypted Data',
        subtitle: 'The most complete platform for training and running AI models on sensitive information using Fully Homomorphic Encryption (FHE).',
        cta: 'Request Demo',
        docs: 'View Documentation'
      },
      metrics: {
        title: 'Platform Metrics',
        items: [
          { value: '559+', label: 'Automated Tests' },
          { value: '15+', label: 'ML Algorithms' },
          { value: '42', label: 'Dashboard Pages' },
          { value: '14', label: 'Backend Apps' },
          { value: '256-bit', label: 'FHE Security' },
          { value: '69%', label: 'Code Coverage' }
        ]
      },
      mlCapabilities: {
        title: 'Machine Learning Capabilities',
        subtitle: 'Over 15 algorithms optimized for homomorphic encryption',
        features: [
          {
            title: 'Encrypted Deep Learning',
            description: 'Neural networks with polynomial activations designed to operate on FHE data.',
            items: ['Feedforward architectures', 'Polynomial activations', 'Secure backpropagation']
          },
          {
            title: 'Ensemble Methods',
            description: 'Combine multiple models for more robust predictions without decrypting data.',
            items: ['Random Forest', 'Gradient Boosting', 'Voting Classifier', 'Stacking']
          },
          {
            title: 'Anomaly Detection',
            description: 'Identify outliers operating completely on encrypted data.',
            items: ['Isolation Forest', 'Local Outlier Factor', 'Statistical detection']
          },
          {
            title: 'Feature Engineering',
            description: 'Advanced FHE-compatible transformers for data preparation.',
            items: ['Polynomial features', 'Categorical encoding', 'Quantile normalization']
          }
        ]
      },
      dashboard: {
        title: 'Enterprise Dashboard',
        subtitle: '42 pages designed for data and compliance teams',
        features: [
          {
            title: 'Report Builder',
            description: 'Generate performance, compliance, and audit reports.',
            items: ['PDF/CSV/Excel/JSON export', 'Scheduled reports', 'Custom templates']
          },
          {
            title: 'Workflow Automation',
            description: 'Configure processing pipelines with automatic triggers.',
            items: ['Scheduled triggers', 'Data-driven events', 'Automatic notifications']
          },
          {
            title: 'Data Explorer',
            description: 'Visualize and analyze encrypted datasets without exposing them.',
            items: ['Secure visualization', 'Aggregated statistics', 'Data preview']
          },
          {
            title: 'Real-time Monitoring',
            description: 'System health dashboards and model metrics.',
            items: ['Configurable alerts', 'Performance metrics', 'Real-time logs']
          }
        ]
      },
      apis: {
        title: 'Enterprise APIs',
        subtitle: 'Complete integration for your systems',
        features: [
          {
            title: 'Batch Prediction',
            description: 'Process thousands of encrypted records in a single API call.',
            items: ['Mass scoring', 'Parallel processing', 'Async results']
          },
          {
            title: 'Model Versioning',
            description: 'Manage the complete lifecycle of your models.',
            items: ['Training → Deploy → Monitor', 'Auto rollback', 'Full traceability']
          },
          {
            title: 'Webhooks',
            description: 'Integrate with external systems via real-time notifications.',
            items: ['Model events', 'Compliance alerts', 'Custom integrations']
          },
          {
            title: 'Model Sharing',
            description: 'Collaborate between organizations without exposing training data.',
            items: ['Pre-trained models', 'Consortium federation', 'Secure transfer']
          }
        ]
      },
      compliance: {
        title: 'Compliance & Security',
        items: [
          'HIPAA, GDPR, PCI-DSS compatible',
          'Security levels: 128, 192 and 256 bits',
          'Immutable audit logs',
          'Optional blockchain integration',
          'Multi-tenant architecture',
          'Complete data isolation'
        ]
      },
      useCases: {
        title: 'Industry Use Cases',
        cases: [
          {
            icon: '🏥',
            title: 'Healthcare',
            description: 'Predictive analytics on encrypted clinical records.',
            examples: ['Cardiovascular risk', 'Early detection', 'Treatment optimization']
          },
          {
            icon: '🏦',
            title: 'Finance',
            description: 'Processing sensitive financial data without exposure.',
            examples: ['Credit scoring', 'Fraud detection', 'Risk analysis']
          },
          {
            icon: '🏛️',
            title: 'Government',
            description: 'Complying with the strictest privacy regulations.',
            examples: ['Tax data', 'Anomaly detection', 'Demand forecasting']
          }
        ]
      },
      cta: {
        title: 'Ready to Transform Your Data?',
        subtitle: 'Request a personalized demo for your industry.',
        button: 'Request Demo',
        contact: 'Contact Sales'
      }
    }
  }

  const t = content[lang]

  // Icons for features
  const icons = {
    brain: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    layers: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
    search: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    ),
    cog: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    document: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    workflow: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
      </svg>
    ),
    chart: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    monitor: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    batch: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
    version: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
      </svg>
    ),
    webhook: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
      </svg>
    ),
    share: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
      </svg>
    )
  }

  const featureIcons = [icons.brain, icons.layers, icons.search, icons.cog]
  const dashboardIcons = [icons.document, icons.workflow, icons.chart, icons.monitor]
  const apiIcons = [icons.batch, icons.version, icons.webhook, icons.share]

  return (
    <div className="min-h-screen bg-slate-50">
      <LandingHeader />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 mb-6">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
              <span className="text-purple-200 text-sm font-medium">{t.hero.badge}</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {t.hero.title}
            </h1>
            <p className="text-xl text-purple-100 mb-10 max-w-3xl mx-auto">
              {t.hero.subtitle}
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://appfhe.xcapit.com/sandbox"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white text-purple-900 px-8 py-4 rounded-xl font-semibold hover:bg-purple-50 transition-colors shadow-xl"
              >
                {t.hero.cta}
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
              <a
                href="https://apifhe.xcapit.com/api/v2/docs/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-white/30 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/10 transition-colors"
              >
                {t.hero.docs}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Metrics Section */}
      <section className="py-16 bg-gradient-to-r from-purple-600 to-indigo-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white text-center mb-10">{t.metrics.title}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {t.metrics.items.map((metric, index) => (
              <MetricCard key={index} value={metric.value} label={metric.label} />
            ))}
          </div>
        </div>
      </section>

      {/* ML Capabilities Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t.mlCapabilities.title}</h2>
            <p className="text-xl text-slate-600">{t.mlCapabilities.subtitle}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.mlCapabilities.features.map((feature, index) => (
              <FeatureCard
                key={index}
                icon={featureIcons[index]}
                title={feature.title}
                description={feature.description}
                items={feature.items}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Dashboard Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t.dashboard.title}</h2>
            <p className="text-xl text-slate-600">{t.dashboard.subtitle}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.dashboard.features.map((feature, index) => (
              <FeatureCard
                key={index}
                icon={dashboardIcons[index]}
                title={feature.title}
                description={feature.description}
                items={feature.items}
              />
            ))}
          </div>
        </div>
      </section>

      {/* APIs Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t.apis.title}</h2>
            <p className="text-xl text-slate-600">{t.apis.subtitle}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.apis.features.map((feature, index) => (
              <FeatureCard
                key={index}
                icon={apiIcons[index]}
                title={feature.title}
                description={feature.description}
                items={feature.items}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-purple-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t.compliance.title}</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {t.compliance.items.map((item, index) => (
              <div key={index} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/20">
                <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="text-white text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t.useCases.title}</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {t.useCases.cases.map((useCase, index) => (
              <UseCaseCard
                key={index}
                icon={useCase.icon}
                title={useCase.title}
                description={useCase.description}
                examples={useCase.examples}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Whitepaper CTA */}
      <WhitepaperCTA />

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-indigo-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t.cta.title}</h2>
          <p className="text-xl text-purple-100 mb-8">{t.cta.subtitle}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://appfhe.xcapit.com/sandbox"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-purple-700 px-8 py-4 rounded-xl font-semibold hover:bg-purple-50 transition-colors shadow-xl"
            >
              {t.cta.button}
            </a>
            <a
              href="mailto:consorcios@xcapit.com"
              className="inline-flex items-center gap-2 border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/10 transition-colors"
            >
              {t.cta.contact}
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-12 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <span className="text-white font-bold text-xl">Xcapit Privacy</span>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/" className="text-slate-400 hover:text-white transition">Home</Link>
              <Link to="/whitepaper" className="text-slate-400 hover:text-white transition">Whitepaper</Link>
              <a href="https://apifhe.xcapit.com/api/v2/docs/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition">API Docs</a>
            </div>
            <p className="text-slate-500 text-sm">© 2026 Xcapit. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
